import type { ExtractAtomValue, Getter } from 'jotai';
import { atom } from 'jotai';
import { atomWithReset, RESET } from 'jotai/utils';
import { v4 as uuid } from 'uuid';
import type { AnnotationLabelT } from '../../../common/types/types';
import type { DraftRelationshipsMapT, OutputRelationshipT, RelationshipLabelDefinitionT, RelationshipStoreWithLabelDataT, RelationshipT } from '../../../modernization-common/relationships';
import { TEMPORAL_SCOPE } from '../../../modernization-common/relationships';
import { resolveFrameRanges } from '../../../modernization-common/relationships/utils/relationship-editor-utils';
import { computeTargetFrameRangeLimits } from '../components/relationship-component/utils';
import type { RelationshipValidationErrors } from '../components/relationship-component/validation-utils';
import { hasDraftValidationErrors, validateResolvedDrafts } from '../components/relationship-component/validation-utils';
import { mapToRelationshipStore } from '../utils/map-to-relationship-store';
import { annotationMetadataAtom, isVideoTaskAtom } from './annotation-atoms';
import { annotationIdToLabelMapAtom } from './annotation-atoms';
import { allAnnotationMapAtom } from './side-panel-atoms';
import { selectedAnnotationIdsAtom } from './tool-atoms';
import { currentFrameAtom } from './video-playback-atoms';
export const relationshipStoreAtom = atom<Record<string, OutputRelationshipT>>({});
export const relationshipLabelsAtom = atom<RelationshipLabelDefinitionT[]>([]);
export type AnnotationWithFrameRangesT = {
  annotationLabel: AnnotationLabelT;
  frameRanges: Array<{
    startFrameIndex: number;
    endFrameIndex: number;
  }>;
};
export const allAnnotationsDataWithFrameRangesAtom = atom<Record<string, AnnotationWithFrameRangesT>>(get => {
  const metadata = get(annotationMetadataAtom);
  const idToLabel = get(annotationIdToLabelMapAtom);
  const result: Record<string, AnnotationWithFrameRangesT> = {};
  for (const [annotationId, m] of metadata) {
    if (m.spans.length === 0) {
      continue;
    }
    const annotationLabel = idToLabel.get(annotationId) ?? {
      title: m.labelId,
      style: {
        strokeColor: '',
        fillColor: '',
        fillOpacity: 0,
        strokeWidth: 0
      }
    };
    result[annotationId] = {
      annotationLabel,
      frameRanges: m.spans.map(({
        start,
        end
      }) => ({
        startFrameIndex: start,
        endFrameIndex: end
      }))
    };
  }
  return result;
});
export const relationshipStoreWithLabelDataAtom = atom(get => {
  const relationships = Object.values(get(relationshipStoreAtom));
  const relationshipLabels = get(relationshipLabelsAtom);
  const allAnnotations = get(allAnnotationsDataWithFrameRangesAtom);
  const relationshipStore: RelationshipStoreWithLabelDataT = mapToRelationshipStore(relationships, relationshipLabels, allAnnotations);
  return relationshipStore;
});
export const currentFrameRelationshipStoreAtom = atom<RelationshipStoreWithLabelDataT>(get => {
  const relationshipStore = get(relationshipStoreWithLabelDataAtom);
  if (!get(isVideoTaskAtom)) {
    return relationshipStore;
  }
  const currentFrame = get(currentFrameAtom);
  return Object.values(relationshipStore).reduce<RelationshipStoreWithLabelDataT>((acc, relationship: RelationshipT) => {
    if (relationship.frameRanges.some(({
      startFrameIndex,
      endFrameIndex
    }) => currentFrame >= startFrameIndex && currentFrame <= endFrameIndex)) {
      acc[relationship.id] = relationship;
    }
    return acc;
  }, {});
});
export const visibleRelationshipsAtom = atom<[string, RelationshipT][]>(get => {
  const store = get(currentFrameRelationshipStoreAtom);
  const selectedIds = get(selectedAnnotationIdsAtom);
  if (selectedIds.length === 0) return [];
  const entries: [string, RelationshipT][] = Object.entries(store);
  return entries.filter(([, relationship]) => selectedIds.includes(relationship.sourceAnnotationId) || selectedIds.includes(relationship.targetAnnotationId));
});
export const selectedAnnotationRelationshipsAtom = atom<RelationshipT[]>(get => {
  const relationshipStore = get(relationshipStoreWithLabelDataAtom);
  const selectedAnnotationIds = get(selectedAnnotationIdsAtom);
  const selectedId = selectedAnnotationIds[0];
  if (!selectedId) return [];
  return Object.values(relationshipStore).filter(r => r.sourceAnnotationId === selectedId);
});

// --- Editor working state atoms ---

export const relationshipEditorOpenAtom = atom<boolean>(false);
export const editingRelationshipAtom = atomWithReset<RelationshipT | undefined>(undefined); // TODO make common with draft with ID

const createDefaultDraftRelationships = (sourceFrameRange?: {
  startFrameIndex: number;
  endFrameIndex: number;
}): DraftRelationshipsMapT => {
  const id = uuid();
  const frameRange = sourceFrameRange ? {
    startFrame: sourceFrameRange.startFrameIndex + 1,
    endFrame: sourceFrameRange.endFrameIndex + 1
  } : {};
  return {
    [id]: {
      id,
      targetId: '',
      frameRanges: [frameRange]
    }
  };
};
export const draftRelationshipsAtom = atomWithReset<DraftRelationshipsMapT>(createDefaultDraftRelationships());
export const activeDraftIdAtom = atomWithReset<string>('');
export const sharedFrameRangeAtom = atomWithReset<{
  startFrame?: number;
  endFrame?: number;
}>({});
export const sameIntervalAtom = atomWithReset<boolean>(false);
export const selectedLabelNameAtom = atomWithReset<string>('');

// Write-only: initialize all editor atoms for create or edit mode
export const relationshipEditorInitAtom = atom(null, (_get, set, params: {
  relationship?: RelationshipT;
  defaultLabelName?: string;
  isSourceEditable?: boolean;
}) => {
  const {
    relationship,
    defaultLabelName,
    isSourceEditable = false
  } = params;
  set(isSourceEditableAtom, isSourceEditable);
  if (isSourceEditable) {
    const selectedId = _get(selectedAnnotationIdsAtom)[0] ?? '';
    const isValidSource = _get(validSourceAnnotationsAtom).some(e => e.annotation.id === selectedId);
    set(sourceObjectIdAtom, isValidSource ? selectedId : '');
  } else {
    set(sourceObjectIdAtom, '');
  }
  const sourceId = _get(sourceObjectIdAtom);
  const allAnnotations = _get(allAnnotationMapAtom);
  const sourceAnnotation = allAnnotations[sourceId];
  const sourceFrameRangeLimits = sourceAnnotation ? {
    startFrame: sourceAnnotation.startFrameIndex + 1,
    endFrame: sourceAnnotation.endFrameIndex + 1
  } : {};
  if (relationship) {
    set(editingRelationshipAtom, relationship);
    set(draftRelationshipsAtom, {
      [relationship.id]: {
        id: relationship.id,
        targetId: relationship.targetAnnotationId,
        frameRanges: relationship.frameRanges.map(r => ({
          startFrame: r.startFrameIndex + 1,
          endFrame: r.endFrameIndex + 1
        }))
      }
    });
    set(activeDraftIdAtom, relationship.id);
    set(selectedLabelNameAtom, relationship.relationshipLabel.name);
    set(sameIntervalAtom, false);
    set(sharedFrameRangeAtom, {});
  } else {
    set(editingRelationshipAtom, RESET);
    const newDrafts = createDefaultDraftRelationships(sourceAnnotation);
    set(draftRelationshipsAtom, newDrafts);
    set(activeDraftIdAtom, Object.keys(newDrafts)[0] ?? '');
    set(selectedLabelNameAtom, defaultLabelName ?? '');
    set(sameIntervalAtom, RESET);
    set(sharedFrameRangeAtom, sourceFrameRangeLimits);
  }
  set(relationshipEditorOpenAtom, true);
});

// Write-only: reset all editor atoms when editor closes
export const relationshipEditorCleanupAtom = atom(null, (_get, set) => {
  set(editingRelationshipAtom, RESET);
  set(draftRelationshipsAtom, RESET);
  set(activeDraftIdAtom, RESET);
  set(sharedFrameRangeAtom, RESET);
  set(sameIntervalAtom, RESET);
  set(selectedLabelNameAtom, RESET);
  set(isSourceEditableAtom, false);
  set(sourceObjectIdAtom, '');
  set(relationshipEditorOpenAtom, false);
});

// --- Editor derived atoms ---

export const selectedLabelAtom = atom(get => {
  const labels = get(relationshipLabelsAtom);
  const name = get(selectedLabelNameAtom);
  return labels.find(l => l.name === name);
});
export const isEditingAtom = atom(get => !!get(editingRelationshipAtom));

// Private helper: resolves all drafts to 0-indexed frame ranges + limits.
// Called by outputRelationshipsAtom. Not exported.
const getResolvedDrafts = (get: Getter) => {
  const selectedLabel = get(selectedLabelAtom);
  const temporalScope = get(isVideoTaskAtom) ? selectedLabel?.temporalConstraints?.temporalScope : undefined;
  const isInterval = temporalScope === TEMPORAL_SCOPE.INTERVAL;
  const sameInterval = get(sameIntervalAtom);
  const sharedFrameRange = get(sharedFrameRangeAtom);
  return Object.values(get(draftRelationshipsAtom)).map(draft => ({
    draftId: draft.id,
    targetId: draft.targetId,
    isInterval,
    frameRanges: resolveFrameRanges(draft, sameInterval, sharedFrameRange, temporalScope)
  }));
};
export const isSourceEditableAtom = atom<boolean>(false);

// Annotations whose label class appears in any relationship label's source constraints
export const validSourceAnnotationsAtom = atom(get => {
  const annotationMap = get(allAnnotationMapAtom);
  const relationshipLabels = get(relationshipLabelsAtom);
  const validSourceClasses = new Set(relationshipLabels.flatMap(l => l.classConstraints.source));
  return Object.values(annotationMap).filter(entry => validSourceClasses.has(entry.annotation.annotationLabel.title));
});
export const sourceObjectIdAtom = (() => {
  //source can either be selected from the relationship editor or is the selected annotation id
  const selectedSourceObjectId = atom<string>('');
  return atom(get => get(isSourceEditableAtom) ? get(selectedSourceObjectId) : get(selectedAnnotationIdsAtom)[0] ?? '', (_get, set, value: string) => {
    set(selectedSourceObjectId, value);
  });
})();
export const relationshipLabelsForSourceAtom = atom<RelationshipLabelDefinitionT[]>(get => {
  const sourceId = get(sourceObjectIdAtom);
  const relationshipLabels = get(relationshipLabelsAtom);
  const sourceClass = get(allAnnotationsDataWithFrameRangesAtom)[sourceId]?.annotationLabel.title;
  return relationshipLabels.filter(label => sourceClass !== undefined && label.classConstraints.source.includes(sourceClass));
});
export const availableTargetsAtom = atom(get => {
  const sourceId = get(sourceObjectIdAtom);
  const sourceClass = get(allAnnotationsDataWithFrameRangesAtom)[sourceId]?.annotationLabel.title ?? '';
  const map = get(allAnnotationMapAtom);
  const selectedRelationshipLabel = get(selectedLabelAtom);
  const allRelationshipLabels = get(relationshipLabelsAtom);
  const targetClasses = new Set<string>();
  if (selectedRelationshipLabel) {
    allRelationshipLabels.filter(relationshipLabel => relationshipLabel.relationshipLabelName === selectedRelationshipLabel.relationshipLabelName && relationshipLabel.temporalConstraints?.temporalScope === selectedRelationshipLabel.temporalConstraints?.temporalScope && relationshipLabel.classConstraints.source.includes(sourceClass)).forEach(relationshipLabel => relationshipLabel.classConstraints.target.forEach(cls => targetClasses.add(cls)));
  }
  return Object.keys(map).filter(id => id !== sourceId).filter(id => {
    if (targetClasses.size === 0) return false;
    const annotation = map[id];
    return annotation && targetClasses.has(annotation.annotation.annotationLabel.title);
  }).map(id => {
    const annotation = map[id];
    const label = annotation ? `${annotation.annotation.annotationLabel.title}[${id}]` : id;
    return {
      id,
      label,
      annotationLabel: annotation?.annotation.annotationLabel
    };
  });
});
export type OutputRelationshipsT = RelationshipT[];

// In edit mode, draft.id === editingRelationship.id (set by relationshipEditorInitAtom),
// so draftId is always the correct relationship ID — no ternary needed.
export const resolvedRelationshipsAtom = atom<RelationshipT[]>(get => {
  const selectedLabel = get(selectedLabelAtom);
  if (!selectedLabel) return [];
  const temporalScope = get(isVideoTaskAtom) ? selectedLabel.temporalConstraints?.temporalScope : undefined;
  const resolvedDrafts = getResolvedDrafts(get);
  // Non-temporal labels need only a target (frameRanges will be []).
  // Temporal labels need at least one resolved frame range (user has entered a start frame).
  const validDrafts = resolvedDrafts.filter(d => d.targetId && (!temporalScope || d.frameRanges.length > 0));
  const sourceObjectId = get(sourceObjectIdAtom);
  return validDrafts.map(draft => ({
    id: draft.draftId,
    sourceAnnotationId: sourceObjectId,
    targetAnnotationId: draft.targetId,
    relationshipLabel: selectedLabel,
    frameRanges: draft.frameRanges
  }));
});

// Derived from resolvedRelationshipsAtom — limits computed at runtime from allAnnotationsWithFrameRangesAtom
// so they always reflect the live source+target intersection (handles edit mode correctly).
export const validationErrorsAtom = atom<RelationshipValidationErrors>(get => {
  const output = get(resolvedRelationshipsAtom);
  if (!output.length) return {};
  const allAnnotations = get(allAnnotationsDataWithFrameRangesAtom);
  const selectedLabel = get(selectedLabelAtom);
  const cardinality = selectedLabel?.cardinalityConstraints?.cardinality;
  const editingRelationship = get(editingRelationshipAtom);
  const sourceId = get(sourceObjectIdAtom);
  const sourceEntry = allAnnotations[sourceId];
  const existingRelationships = Object.values(get(relationshipStoreWithLabelDataAtom)).filter(r => r.relationshipLabel.name === selectedLabel?.name && r.id !== editingRelationship?.id);
  return validateResolvedDrafts(output.map(rel => {
    const temporalScope = rel.relationshipLabel.temporalConstraints?.temporalScope;
    const targetEntry = allAnnotations[rel.targetAnnotationId];
    const {
      frameLimits: computedLimits
    } = computeTargetFrameRangeLimits(sourceEntry?.frameRanges ?? [], targetEntry?.frameRanges ?? []);
    return {
      draftId: rel.id,
      targetId: rel.targetAnnotationId,
      isInterval: temporalScope === TEMPORAL_SCOPE.INTERVAL,
      frameRanges: rel.frameRanges,
      frameLimits: computedLimits.filter(l => l.startFrame !== undefined && l.endFrame !== undefined).map(l => ({
        startFrameIndex: l.startFrame! - 1,
        endFrameIndex: l.endFrame! - 1
      })),
      targetAnnotationFrameRanges: targetEntry?.frameRanges ?? []
    };
  }), {
    cardinality,
    sourceId,
    existingRelationships
  });
});
export const hasValidationErrorsAtom = atom<boolean>(get => hasDraftValidationErrors(get(validationErrorsAtom)));
export type RelationshipAtomHydrationList = [typeof relationshipStoreAtom, ExtractAtomValue<typeof relationshipStoreAtom>] | [typeof relationshipLabelsAtom, ExtractAtomValue<typeof relationshipLabelsAtom>];